import { useEffect, useState } from 'react';
import { codeLabel } from '../../utils/codeLabel';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { safePagePlaceholder, staticQueryPolicy } from '../../api/queryPolicies';
import { getChangeRequests, getUsers } from '../../api/endpoints/admin';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import type { AdminUserResponse, ProfileChangeRequest, UserRole, KycStatus } from '../../api/types';
import { DataTable } from '../../components/ui/DataTable';
import { Pagination } from '../../components/ui/Pagination';
import { RefreshButton } from '../../components/ui/RefreshButton';
import { StatusPill } from '../../components/ui/StatusPill';
import { kycStatusColor } from '../../components/domain/statusColor';
import { sectionLabel } from '../../components/domain/changeRequestDisplay';
import { formatIstDayMon } from '../payments/paymentDates';
import { userDisplayName } from '../../components/domain/partyLabel';
import { UserDetailDrawer } from '../../components/domain/UserDetailDrawer';
import { UserFormModal } from '../../components/domain/UserFormModal';
import { VendorImportModal } from '../../components/domain/VendorImportModal';
import { ShipperImportModal } from '../../components/domain/ShipperImportModal';
import styles from './UsersView.module.css';

interface Props {
  role: UserRole;
}

const KYC_FILTER_TABS: { label: string; value: KycStatus | 'ALL' }[] = [
  { label: 'All', value: 'ALL' },
  { label: 'Submitted', value: 'SUBMITTED' },
  { label: 'Pending', value: 'PENDING' },
  { label: 'Verified', value: 'VERIFIED' },
  { label: 'Rejected', value: 'REJECTED' },
];

/** The "Change requests" filter (#281): PENDING profile change requests, not a KYC status. */
const CHANGE_REQUESTS = 'CHANGE_REQUESTS';
type UsersFilter = KycStatus | 'ALL' | typeof CHANGE_REQUESTS;
/** The API caps `size` at 100; one page is enough for a count and an oldest-first list. */
const CHANGE_REQUEST_PAGE = 100;
const DAY_MS = 24 * 60 * 60 * 1000;

function waitingDays(iso: string | undefined): string {
  const t = iso ? new Date(iso).getTime() : NaN;
  if (Number.isNaN(t)) return '—';
  const days = Math.max(0, Math.floor((Date.now() - t) / DAY_MS));
  return days === 0 ? 'Today' : days === 1 ? '1 day' : `${days} days`;
}

function fmtDate(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function UsersView({ role }: Props) {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300, () => setPage(0));
  const [kycFilter, setKycFilter] = useState<UsersFilter>('ALL');
  const showChangeRequests = kycFilter === CHANGE_REQUESTS;
  const navigate = useNavigate();
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);

  // Deep-link support: /shippers?open=<userId> opens the detail drawer directly.
  const [searchParams, setSearchParams] = useSearchParams();
  const openParam = searchParams.get('open');
  useEffect(() => {
    if (openParam && !Number.isNaN(Number(openParam))) {
      setSelectedUserId(Number(openParam));
    }
  }, [openParam]);

  function closeDrawer() {
    setSelectedUserId(null);
    if (searchParams.has('open')) {
      searchParams.delete('open');
      setSearchParams(searchParams, { replace: true });
    }
  }

  function handleSearch(v: string) {
    setSearch(v);
  }

  // Shippers and partners are businesses: the company leads, the contact person follows.
  const hasCompany = role === 'SHIPPER' || role === 'FLEET_MANAGER';

  const queryKey = ['admin', 'users', role, page, debouncedSearch, kycFilter] as const;
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey,
    queryFn: () =>
      getUsers({
        page,
        size: 25,
        role,
        search: debouncedSearch || undefined,
        kycStatus: kycFilter === 'ALL' || kycFilter === CHANGE_REQUESTS ? undefined : kycFilter,
      }),
    enabled: !showChangeRequests,
    ...staticQueryPolicy,
    placeholderData: safePagePlaceholder(queryKey, 3),
  });

  // One call answers both the tab count and the list; filtered to this role (no role filter on the API).
  const changeRequestsQuery = useQuery({
    queryKey: ['admin', 'change-requests', 'PENDING'],
    queryFn: () => getChangeRequests({ status: 'PENDING', page: 0, size: CHANGE_REQUEST_PAGE }),
    ...staticQueryPolicy,
  });
  const roleRequests: ProfileChangeRequest[] = (changeRequestsQuery.data?.content ?? []).filter(
    (r) => r.userRole === role,
  );
  const requestCount =
    (changeRequestsQuery.data?.totalElements ?? 0) > CHANGE_REQUEST_PAGE
      ? `${CHANGE_REQUEST_PAGE}+`
      : roleRequests.length > 0
        ? String(roleRequests.length)
        : null;
  const openKyc = (r: ProfileChangeRequest) => navigate(`/users/${r.userId}?tab=kyc`);

  return (
    <div className={styles.page}>
      {/* Search Input */}
      <div className={styles.toolbar}>
        <input
          id={`users-search-${role}`}
          className={styles.search}
          type="search"
          placeholder={
            hasCompany
              ? `Search ${role === 'FLEET_MANAGER' ? 'partners' : 'shippers'} by company, name, email or phone…`
              : `Search ${role.toLowerCase()}s by name, email, or phone…`
          }
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
        />
        <RefreshButton
          queryKeys={[
            ['admin', 'users'],
            ['admin', 'change-requests'],
          ]}
        />
        {(role === 'FLEET_MANAGER' || role === 'SHIPPER') && (
          <button className={styles.addBtn} onClick={() => setIsImportOpen(true)}>
            {t('users.usersView.importSpreadsheet')}
          </button>
        )}
        <button className={styles.addBtn} onClick={() => setIsAddOpen(true)}>
          {t('users.usersView.add', { role: role === 'FLEET_MANAGER' ? 'partner' : role.toLowerCase() })}
        </button>
      </div>

      {/* KYC Status Filter Tabs */}
      <div className={styles.filterTabs} role="tablist" aria-label={t('users.usersView.kycFilterLabel')}>
        {KYC_FILTER_TABS.map((tab) => (
          <button
            key={tab.value}
            role="tab"
            aria-selected={kycFilter === tab.value}
            className={`${styles.filterTab} ${kycFilter === tab.value ? styles.filterTabActive : ''}`}
            onClick={() => {
              setKycFilter(tab.value);
              setPage(0);
            }}
          >
            {tab.label}
          </button>
        ))}
        <button
          role="tab"
          aria-selected={showChangeRequests}
          className={`${styles.filterTab} ${showChangeRequests ? styles.filterTabActive : ''}`}
          onClick={() => setKycFilter(CHANGE_REQUESTS)}
        >
          {t('users.usersView.changeRequests')}
          {requestCount && (
            <span className={styles.tabCount}>
              <StatusPill
                bg="var(--status-amber-bg)"
                text="var(--status-amber-text)"
                label={requestCount}
              />
            </span>
          )}
        </button>
      </div>

      {showChangeRequests && (
        <DataTable
          loading={changeRequestsQuery.isLoading}
          error={changeRequestsQuery.isError ? 'We couldn’t load change requests' : undefined}
          onRetry={() => changeRequestsQuery.refetch()}
          keyFn={(r) => r.id}
          rows={roleRequests}
          empty="No change requests waiting."
          onRowClick={openKyc}
          columns={[
            {
              key: 'user',
              header: 'User',
              render: (r) => (
                <button className={styles.nameBtn} onClick={() => openKyc(r)}>
                  {r.userFullName?.trim() || 'No name'}
                </button>
              ),
            },
            {
              key: 'phone',
              header: 'Phone',
              width: '150px',
              render: (r) => r.userPhoneNumber || '—',
            },
            { key: 'section', header: 'Section', render: (r) => sectionLabel(r.section) },
            {
              key: 'requested',
              header: 'Requested',
              width: '130px',
              render: (r) =>
                r.createdAt && !Number.isNaN(new Date(r.createdAt).getTime())
                  ? formatIstDayMon(r.createdAt)
                  : '—',
            },
            {
              key: 'waiting',
              header: 'Waiting',
              width: '100px',
              render: (r) => waitingDays(r.createdAt),
            },
          ]}
        />
      )}

      {!showChangeRequests && (
        <DataTable
          loading={isLoading}
          error={isError ? 'We couldn’t load users' : undefined}
          onRetry={() => refetch()}
          keyFn={(u) => u.id}
          rows={data?.items ?? []}
          empty={
            kycFilter !== 'ALL'
              ? `No ${role.toLowerCase()}s found with KYC status ${kycFilter}`
              : `No ${role.toLowerCase()}s found`
          }
          columns={[
            {
              key: 'id',
              header: 'ID',
              width: '64px',
              render: (u) => <span className={styles.mono}>#{u.id}</span>,
            },
            ...(hasCompany
              ? [
                  {
                    key: 'company',
                    header: 'Company',
                    render: (u: AdminUserResponse) => {
                      const company = u.companyName?.trim();
                      return company ? (
                        <button
                          className={`${styles.nameBtn} ${styles.companyBtn}`}
                          title={company}
                          onClick={() => setSelectedUserId(u.id)}
                        >
                          {company}
                        </button>
                      ) : (
                        '—'
                      );
                    },
                  },
                ]
              : []),
            {
              key: 'name',
              header: 'Name',
              render: (u) => {
                const name = userDisplayName(u);
                // With a company the Name column is the contact person; without one it is the headline.
                const label = u.companyName?.trim() ? name.secondary : name.headline;
                return label ? (
                  <button className={styles.nameBtn} onClick={() => setSelectedUserId(u.id)}>
                    {label}
                  </button>
                ) : (
                  '—'
                );
              },
            },
            { key: 'phone', header: 'Phone', width: '150px', render: (u) => u.phoneNumber },
            { key: 'email', header: 'Email', render: (u) => u.email || '—' },
            {
              key: 'createdAt',
              header: 'Joined',
              width: '120px',
              render: (u) => fmtDate(u.createdAt),
            },
            {
              key: 'kyc',
              header: 'KYC Status',
              width: '130px',
              render: (u) => {
                const c = kycStatusColor(u.kycStatus as KycStatus);
                return <StatusPill bg={c.bg} text={c.text} label={codeLabel('kyc', u.kycStatus)} />;
              },
            },
            {
              key: 'status',
              header: 'Status',
              width: '110px',
              render: (u) => (
                <span className={u.isActive ? styles.statusActive : styles.statusInactive}>
                  {codeLabel('userStatus', u.status)}
                </span>
              ),
            },
          ]}
        />
      )}

      {!showChangeRequests && data && (
        <Pagination
          page={data.page}
          totalPages={data.totalPages}
          totalItems={data.totalItems}
          size={data.size}
          onPage={setPage}
        />
      )}

      {selectedUserId !== null && (
        <UserDetailDrawer userId={selectedUserId} onClose={closeDrawer} />
      )}

      {isAddOpen && role !== 'ADMIN' && (
        <UserFormModal role={role} onClose={() => setIsAddOpen(false)} />
      )}

      {isImportOpen && role === 'FLEET_MANAGER' && (
        <VendorImportModal onClose={() => setIsImportOpen(false)} />
      )}

      {isImportOpen && role === 'SHIPPER' && (
        <ShipperImportModal onClose={() => setIsImportOpen(false)} />
      )}
    </div>
  );
}
